/* eslint-disable @typescript-eslint/no-explicit-any */
import * as fabric from 'fabric';

export type Tool = 'select' | 'rectangle' | 'polygon';

export interface IAnnotationData {
	id: string;
	type: string;
	label: string;
	tool: string;
	visible: boolean;
	locked: boolean;
	coords: any;
}

interface IAnnotationMeta {
	id: string;
	label: string;
	tool: string;
	visible: boolean;
	locked: boolean;
}

interface IAnnotatedFabricObject extends fabric.Object {
	data?: IAnnotationMeta;
	name?: string;
}

const canvasInstances = new WeakMap<HTMLCanvasElement, FabricManager>();

export class FabricManager {
	private canvas: fabric.Canvas;
	private onCreateCallback?: (data: IAnnotationData) => void;
	private onRemoveCallback?: (id: string) => void;
	private onMessageCallback?: (msg: string) => void;
	private onUpdateCallback?: (data: IAnnotationData[]) => void;
	private onToolChangeCallback?: (tool: Tool) => void;
	private currentTool?: string;
	private highlightTimeouts = new Map<string, number>();
	private toolLabelCounters: Record<string, number> = {};

	private isDrawingRect = false;
	private isDrawingPolygon = false;

	// Panning/Zooming Handlers
	private panIsDragging = false;
	private lastPanPosX = 0;
	private lastPanPosY = 0;
	private onPanMouseDown = (
		_opt: fabric.TEvent<MouseEvent | TouchEvent>,
	) => {};
	private onPanMouseMove = (
		_opt: fabric.TEvent<MouseEvent | TouchEvent>,
	) => {};
	private onPanMouseUp = () => {};
	private onMouseWheel = (_opt: fabric.TEvent<WheelEvent>) => {};

	// Drawing Handlers
	private onRectMouseDown = (
		_opt: fabric.TPointerEventInfo<fabric.TPointerEvent>,
	) => {};
	private onRectMouseMove = (
		_opt: fabric.TPointerEventInfo<fabric.TPointerEvent>,
	) => {};
	private onRectMouseUp = () => {};
	private onPolygonMouseDown = (
		_opt: fabric.TPointerEventInfo<fabric.TPointerEvent>,
	) => {};
	private onPolygonMouseMove = (
		_opt: fabric.TPointerEventInfo<fabric.TPointerEvent>,
	) => {};
	private onPolygonDblClick = () => {};
	private onPolygonKeyDown = (_e: KeyboardEvent) => {};

	private polygonPoints: fabric.Point[] = [];
	private polygonLines: fabric.Line[] = [];
	private polygonVertices: fabric.Circle[] = [];

	private imageScale = 1;
	private imageOffset = { x: 0, y: 0 };
	private originalImageDimensions = { width: 0, height: 0 };

	constructor(canvasEl: HTMLCanvasElement) {
		// Check for an existing instance using a WeakMap
		const existingInstance = canvasInstances.get(canvasEl);
		if (existingInstance) {
			console.warn(
				'Fabric canvas already initialized on this element. Disposing old instance.',
			);
			existingInstance.dispose();
			canvasInstances.delete(canvasEl);
		}

		this.canvas = new fabric.Canvas(canvasEl, {
			selection: true,
			preserveObjectStacking: true,
		});

		// Store the new instance in the WeakMap
		canvasInstances.set(canvasEl, this);

		this.setupZoomAndPan();
		this.bindEvents();
	}

	private getDefaultAnnotationData() {
		const tool = this.currentTool || 'unknown';
		if (!this.toolLabelCounters[tool]) {
			this.toolLabelCounters[tool] = 1;
		}
		const label = `${tool}_${this.toolLabelCounters[tool]++}`;

		return {
			id: `ann-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
			label,
			tool,
			visible: true,
			locked: false,
		};
	}

	private bindEvents() {
		this.canvas.on('object:added', (e) => {
			const target = e.target as IAnnotatedFabricObject | undefined;
			if (!target || target.data?.id) return;
			if (target.get('name') === 'polygon-helper') return;

			target.set('data', this.getDefaultAnnotationData());

			this.onCreateCallback?.(this.extractAnnotationData(target));
		});

		this.canvas.on('object:removed', (e) => {
			const target = e.target as IAnnotatedFabricObject | undefined;
			if (!target?.data?.id) return;

			this.onRemoveCallback?.(target.data.id);
			delete target.data;
		});
	}

	private setupZoomAndPan() {
		this.onMouseWheel = (opt) => {
			const delta = opt.e.deltaY;
			let zoom = this.canvas.getZoom();
			zoom *= 0.999 ** delta;
			zoom = Math.min(Math.max(zoom, 0.5), 5);
			this.canvas.zoomToPoint(
				new fabric.Point(opt.e.offsetX, opt.e.offsetY),
				zoom,
			);
			opt.e.preventDefault();
			opt.e.stopPropagation();
		};

		this.onPanMouseDown = (opt) => {
			const e = opt.e as MouseEvent | TouchEvent;
			// Panning is active on middle mouse button or Shift+click
			if ('button' in e && (e.button === 1 || e.shiftKey)) {
				e.preventDefault();
				this.panIsDragging = true;
				if ('clientX' in e && 'clientY' in e) {
					this.lastPanPosX = e.clientX;
					this.lastPanPosY = e.clientY;
				}
				this.canvas.setCursor('grab');
				this.canvas.selection = false;
			}
		};

		this.onPanMouseMove = (opt) => {
			if (!this.panIsDragging) return;
			const e = opt.e as MouseEvent | TouchEvent;
			if ('clientX' in e && 'clientY' in e) {
				const vpt = this.canvas.viewportTransform!;
				vpt[4] += e.clientX - this.lastPanPosX;
				vpt[5] += e.clientY - this.lastPanPosY;
				this.canvas.requestRenderAll();
				this.lastPanPosX = e.clientX;
				this.lastPanPosY = e.clientY;
			}
		};

		this.onPanMouseUp = () => {
			this.panIsDragging = false;
			this.canvas.setCursor('default');
			this.canvas.selection = true;
		};

		this.canvas.on('mouse:wheel', this.onMouseWheel);
		this.canvas.on('mouse:down', this.onPanMouseDown);
		this.canvas.on('mouse:move', this.onPanMouseMove);
		this.canvas.on('mouse:up', this.onPanMouseUp);
	}

	private enableRectangleDrawing() {
		if (this.isDrawingRect) return;
		this.isDrawingRect = true;

		let isDown = false;
		let origX = 0;
		let origY = 0;
		let rect: fabric.Rect;

		this.onRectMouseDown = (o) => {
			isDown = true;
			const pointer = this.canvas.getPointer(o.e);
			origX = pointer.x;
			origY = pointer.y;
			rect = new fabric.Rect({
				left: origX,
				top: origY,
				width: 0,
				height: 0,
				fill: 'rgba(0,0,0,0.1)',
				stroke: 'red',
				strokeWidth: 1.5,
				selectable: true,
				lockRotation: true,
			});
			this.canvas.add(rect);
		};

		this.onRectMouseMove = (o) => {
			if (!isDown) return;
			const pointer = this.canvas.getPointer(o.e);
			rect.set({
				width: Math.abs(pointer.x - origX),
				height: Math.abs(pointer.y - origY),
				left: pointer.x > origX ? origX : pointer.x,
				top: pointer.y > origY ? origY : pointer.y,
			});
			this.canvas.renderAll();
		};

		this.onRectMouseUp = () => {
			isDown = false;

			const { width, height } = rect;
			if (!width || !height) {
				this.canvas.remove(rect);
				this.onMessageCallback?.('Rectangle too small. Try again.');

				if (
					this.toolLabelCounters['rectangle'] &&
					this.toolLabelCounters['rectangle'] > 0
				) {
					this.toolLabelCounters['rectangle']--;
				}
			}
		};

		this.canvas.on('mouse:down', this.onRectMouseDown);
		this.canvas.on('mouse:move', this.onRectMouseMove);
		this.canvas.on('mouse:up', this.onRectMouseUp);
	}

	private disableRectangleDrawing() {
		if (!this.isDrawingRect) return;
		this.isDrawingRect = false;

		this.canvas.off('mouse:down', this.onRectMouseDown);
		this.canvas.off('mouse:move', this.onRectMouseMove);
		this.canvas.off('mouse:up', this.onRectMouseUp);
	}

	private enablePolygonDrawing() {
		if (this.isDrawingPolygon) return;
		this.isDrawingPolygon = true;

		this.polygonPoints = [];
		this.polygonLines = [];
		this.polygonVertices = [];

		const CLOSE_DISTANCE = 2;

		const distance = (p1: fabric.Point, p2: fabric.Point) => {
			return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
		};

		this.onPolygonKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				this.cancelCurrentPolygonDrawing();
			}
		};

		const finishPolygon = () => {
			if (this.polygonPoints.length < 3) return;

			const polygon = new fabric.Polygon(this.polygonPoints, {
				fill: 'rgba(0,0,255,0.1)',
				stroke: 'blue',
				strokeWidth: 2,
				selectable: true,
				lockRotation: true,
			}) as IAnnotatedFabricObject;
			polygon.data = this.getDefaultAnnotationData();

			// Remove temp circles and lines
			this.polygonLines.forEach((line) => this.canvas.remove(line));
			this.polygonVertices.forEach((c) => this.canvas.remove(c));
			this.canvas.add(polygon);

			// Fire callback
			this.onCreateCallback?.(this.extractAnnotationData(polygon));

			// Reset for next polygon
			this.polygonLines = [];
			this.polygonPoints = [];
			this.polygonVertices = [];

			this.canvas.requestRenderAll();

			this.setTool('select');
			this.onToolChangeCallback?.('select');
		};

		this.onPolygonDblClick = () => {
			finishPolygon();
		};

		this.onPolygonMouseDown = (opt) => {
			const pointer = this.canvas.getPointer(opt.e);
			const point = new fabric.Point(pointer.x, pointer.y);

			if (
				this.polygonPoints.length > 2 &&
				distance(point, this.polygonPoints[0]) < CLOSE_DISTANCE
			) {
				// Close polygon if clicked near first point
				finishPolygon();
				return;
			}

			this.polygonPoints.push(point);

			const circle = new fabric.Circle({
				left: point.x,
				top: point.y,
				radius: 2,
				fill: 'red',
				selectable: false,
				evented: false,
				originX: 'center',
				originY: 'center',
			});
			circle.set('name', 'polygon-helper');
			this.canvas.add(circle);
			this.polygonVertices.push(circle);

			if (this.polygonPoints.length > 1) {
				const from = this.polygonPoints[this.polygonPoints.length - 2];
				const to = point;
				const line = new fabric.Line([from.x, from.y, to.x, to.y], {
					stroke: 'red',
					selectable: false,
					evented: false,
				});
				line.set('name', 'polygon-helper');
				this.canvas.add(line);
				this.polygonLines.push(line);
			}
		};

		window.addEventListener('keydown', this.onPolygonKeyDown);
		this.canvas.on('mouse:down', this.onPolygonMouseDown);
		this.canvas.on('mouse:move', this.onPolygonMouseMove);
		this.canvas.on('mouse:dblclick', this.onPolygonDblClick);
	}

	private round(num: number, precision = 6) {
		return parseFloat(num.toFixed(precision));
	}

	private disablePolygonDrawing() {
		if (!this.isDrawingPolygon) return;
		this.isDrawingPolygon = false;

		window.removeEventListener('keydown', this.onPolygonKeyDown);
		this.canvas.off('mouse:down', this.onPolygonMouseDown);
		this.canvas.off('mouse:dblclick', this.onPolygonDblClick);

		this.polygonLines.forEach((line) => this.canvas.remove(line));
		this.polygonVertices.forEach((circle) => this.canvas.remove(circle));

		// Clear the polygon drawing buffers
		this.polygonLines = [];
		this.polygonVertices = [];
		this.polygonPoints = [];

		this.canvas.requestRenderAll();
	}

	private cancelCurrentPolygonDrawing() {
		this.polygonLines.forEach((line) => this.canvas.remove(line));
		this.polygonVertices.forEach((circle) => this.canvas.remove(circle));

		this.polygonLines = [];
		this.polygonVertices = [];
		this.polygonPoints = [];

		this.canvas.requestRenderAll();
	}

	private extractAnnotationData(obj: fabric.Object): IAnnotationData {
		const annotated = obj as IAnnotatedFabricObject;
		const data = annotated.data;

		if (!this.canvas.backgroundImage) return data as IAnnotationData;

		const img = this.canvas.backgroundImage as fabric.Image;
		const imgWidth = img.width! * this.imageScale;
		const imgHeight = img.height! * this.imageScale;

		const toImageRelative = (x: number, y: number) => ({
			x: this.round(
				(x - this.imageOffset.x) / (img.width! * this.imageScale),
			),
			y: this.round(
				(y - this.imageOffset.y) / (img.height! * this.imageScale),
			),
		});

		if (obj.type === 'polygon') {
			const polygon = obj as fabric.Polygon;
			const scaleX = polygon.scaleX ?? 1;
			const scaleY = polygon.scaleY ?? 1;

			const transformedPoints = polygon
				.get('points')!
				.map((p: any) => new fabric.Point(p.x * scaleX, p.y * scaleY));

			const relativePoints = transformedPoints.map((p: any) =>
				toImageRelative(p.x, p.y),
			);
			const relativeLeftTop = toImageRelative(
				polygon.left!,
				polygon.top!,
			);

			return {
				id: data?.id || 'unknown',
				type: 'polygon',
				label: data?.label || '',
				tool: data?.tool || '',
				visible: annotated.visible ?? true,
				locked: annotated.selectable === false,
				coords: {
					left: relativeLeftTop.x,
					top: relativeLeftTop.y,
					points: relativePoints,
				},
			};
		}

		const rect = annotated as fabric.Rect;
		const topLeft = toImageRelative(rect.left!, rect.top!);
		const actualWidth = (rect.width ?? 0) * (rect.scaleX ?? 1);
		const actualHeight = (rect.height ?? 0) * (rect.scaleY ?? 1);

		return {
			id: data?.id || 'unknown',
			type: 'rectangle',
			label: data?.label || '',
			tool: data?.tool || '',
			visible: annotated.visible ?? true,
			locked: annotated.selectable === false,
			coords: {
				left: topLeft.x,
				top: topLeft.y,
				width: this.round(actualWidth / imgWidth),
				height: this.round(actualHeight / imgHeight),
			},
		};
	}

	private applyBackgroundScale(canvasWidth: number, canvasHeight: number) {
		const bgImg = this.canvas.backgroundImage as fabric.Image;
		if (!bgImg || !bgImg.width || !bgImg.height) return;

		const imgWidth = bgImg.width;
		const imgHeight = bgImg.height;

		const scaleX = canvasWidth / imgWidth;
		const scaleY = canvasHeight / imgHeight;
		this.imageScale = Math.min(scaleX, scaleY);

		bgImg.scale(this.imageScale);

		this.imageOffset.x = (canvasWidth - imgWidth * this.imageScale) / 2;
		this.imageOffset.y = (canvasHeight - imgHeight * this.imageScale) / 2;

		bgImg.left = this.imageOffset.x + (imgWidth * this.imageScale) / 2;
		bgImg.top = this.imageOffset.y + (imgHeight * this.imageScale) / 2;
		bgImg.originX = 'center';
		bgImg.originY = 'center';

		bgImg.setCoords();
	}

	public async setBackgroundImage(imageUrl: string): Promise<void> {
		try {
			const img = await fabric.Image.fromURL(imageUrl);

			const canvasWidth = this.canvas.getWidth();
			const canvasHeight = this.canvas.getHeight();

			this.originalImageDimensions.width = img.width ?? 0;
			this.originalImageDimensions.height = img.height ?? 0;

			this.canvas.backgroundImage = img;
			this.applyBackgroundScale(canvasWidth, canvasHeight);
			this.canvas.requestRenderAll();
		} catch (error) {
			console.error('Error loading background image:', error);
			this.onMessageCallback?.('Error: Could not load the image.');
		}
	}

	public setTool(tool: Tool) {
		this.currentTool = tool;
		this.canvas.isDrawingMode = false;
		this.canvas.selection = true;

		// Disable any active drawing tools first
		this.disableRectangleDrawing();
		this.disablePolygonDrawing();

		// Enable the selected tool
		switch (tool) {
			case 'rectangle':
				this.enableRectangleDrawing();
				break;
			case 'polygon':
				this.enablePolygonDrawing();
				break;
			case 'select':
			default:
				break;
		}
	}

	public onAnnotationCreated(cb: (data: IAnnotationData) => void) {
		this.onCreateCallback = cb;
	}

	public onToolChange(cb: (tool: Tool) => void) {
		this.onToolChangeCallback = cb;
	}

	public onAnnotationRemoved(cb: (id: string) => void) {
		this.onRemoveCallback = cb;
	}

	public onAnnotationsUpdated(cb: (data: IAnnotationData[]) => void) {
		this.onUpdateCallback = cb;
	}

	public onMessage(cb: (msg: string) => void) {
		this.onMessageCallback = cb;
	}

	public updateAnnotationLabel(id: string, label: string) {
		const obj = this.canvas
			.getObjects()
			.find((o) => (o as IAnnotatedFabricObject).data?.id === id);
		if (obj && (obj as IAnnotatedFabricObject).data) {
			(obj as IAnnotatedFabricObject).data!.label = label;
		}
	}

	public toggleVisibility(id: string) {
		const obj = this.canvas
			.getObjects()
			.find((o: any) => o.data?.id === id);
		if (obj) {
			obj.visible = !obj.visible;
			if (!obj.visible && this.canvas.getActiveObject() === obj) {
				this.canvas.discardActiveObject();
			}
			this.canvas.requestRenderAll();
		}
	}

	public toggleLock(id: string) {
		const obj = this.canvas
			.getObjects()
			.find((o) => (o as IAnnotatedFabricObject).data?.id === id) as
			| IAnnotatedFabricObject
			| undefined;

		if (obj && obj.data) {
			obj.selectable = obj.evented = !obj.selectable;
			obj.data.locked = !obj.data.locked;
		}

		if (obj && this.canvas.getActiveObject() === obj) {
			this.canvas.discardActiveObject();
		}
		this.canvas.renderAll();
	}

	public lockAnnotation(id: string) {
		const obj = this.canvas
			.getObjects()
			.find((o) => (o as IAnnotatedFabricObject).data?.id === id) as
			| IAnnotatedFabricObject
			| undefined;

		if (obj && obj.data) {
			obj.selectable = obj.evented = false;
			obj.data.locked = true;
		}

		if (obj && this.canvas.getActiveObject() === obj) {
			this.canvas.discardActiveObject();
		}
		this.canvas.renderAll();
	}

	public highlightAnnotation(id: string) {
		const object = this.canvas
			.getObjects()
			.find((obj: any) => obj.data?.id === id) as fabric.Object;

		if (!object) return;

		if (this.highlightTimeouts.has(id)) return;

		const originalStroke = object.stroke;
		const originalStrokeWidth = object.strokeWidth;
		const originalStrokeDashArray = object.strokeDashArray;

		// Bring to front and temporarily change appearance
		if ('bringToFront' in object) {
			(object as any).bringToFront();
		}

		object.set({
			stroke: 'yellow',
			strokeWidth: 3,
			strokeDashArray: [5, 5],
		});

		if (object.visible && object.selectable) {
			this.canvas.setActiveObject(object);
		}

		this.canvas.requestRenderAll();

		// Revert styles after delay and clear the timeout reference
		const timeoutId = window.setTimeout(() => {
			object.set({
				stroke: originalStroke,
				strokeWidth: originalStrokeWidth,
				strokeDashArray: originalStrokeDashArray,
			});
			this.canvas.requestRenderAll();
			this.highlightTimeouts.delete(id);
		}, 300);

		this.highlightTimeouts.set(id, timeoutId);
	}

	public deleteAnnotation(id: string) {
		const obj = this.canvas
			.getObjects()
			.find((o) => (o as IAnnotatedFabricObject).data?.id === id);
		if (obj) {
			this.canvas.remove(obj);
			this.canvas.requestRenderAll();
		}
		this.canvas.requestRenderAll();
	}

	public exportAnnotations(): IAnnotationData[] {
		return this.canvas
			.getObjects()
			.map((obj: any) => this.extractAnnotationData(obj));
	}

	public getLatestDataForAnnotation(id: string) {
		const obj = this.canvas
			.getObjects()
			.find((o: IAnnotatedFabricObject) => o.data?.id === id);

		return obj ? this.extractAnnotationData(obj) : null;
	}

	public importAnnotations(data: IAnnotationData[]) {
		// Clear existing annotations
		this.canvas.getObjects().forEach((obj) => {
			if ((obj as IAnnotatedFabricObject)?.data?.id) {
				this.canvas.remove(obj);
			}
		});

		if (!this.canvas.backgroundImage) return;

		const img = this.canvas.backgroundImage as fabric.Image;
		const imgWidth = img.width! * this.imageScale;
		const imgHeight = img.height! * this.imageScale;

		const fromImageRelative = (x: number, y: number) => ({
			x: this.round(x * imgWidth + this.imageOffset.x),
			y: this.round(y * imgHeight + this.imageOffset.y),
		});

		data.forEach((ann) => {
			if (ann.type.includes('rect')) {
				const leftTop = fromImageRelative(
					ann.coords.left,
					ann.coords.top,
				);
				const absWidth = this.round(ann.coords.width * imgWidth);
				const absHeight = this.round(ann.coords.height * imgHeight);

				const rect = new fabric.Rect({
					left: leftTop.x,
					top: leftTop.y,
					width: absWidth,
					height: absHeight,
					fill: 'rgba(0,0,0,0.1)',
					stroke: 'red',
					strokeWidth: 2,
					selectable: !ann.locked,
					visible: ann.visible,
					objectCaching: false,
					scaleX: 1,
					scaleY: 1,
					lockRotation: true,
				});
				rect.set('data', ann);
				this.canvas.add(rect);
			} else if (ann.type === 'polygon') {
				const absPoints = ann.coords.points.map((p: any) =>
					fromImageRelative(p.x, p.y),
				);
				const absLeftTop = fromImageRelative(
					ann.coords.left,
					ann.coords.top,
				);

				const polygon = new fabric.Polygon(absPoints, {
					left: absLeftTop.x,
					top: absLeftTop.y,
					fill: 'rgba(0,0,255,0.1)',
					stroke: 'blue',
					strokeWidth: 2,
					selectable: !ann.locked,
					visible: ann.visible,
					objectCaching: false,
					scaleX: 1,
					scaleY: 1,
					lockRotation: true,
				});
				polygon.set('data', ann);
				this.canvas.add(polygon);
			}
		});

		this.canvas.requestRenderAll();
	}

	public resizeCanvas(width: number, height: number) {
		const exportedData = this.exportAnnotations();

		this.canvas.setWidth(width);
		this.canvas.setHeight(height);
		this.canvas.calcOffset();

		if (this.canvas.backgroundImage) {
			this.applyBackgroundScale(width, height);
		}

		this.importAnnotations(exportedData);

		this.onUpdateCallback?.(exportedData);

		this.canvas.requestRenderAll();
	}

	public dispose() {
		this.disableRectangleDrawing();
		this.disablePolygonDrawing();

		this.highlightTimeouts.forEach((timeoutId) =>
			window.clearTimeout(timeoutId),
		);
		this.highlightTimeouts.clear();

		this.canvas.off('mouse:wheel', this.onMouseWheel);
		this.canvas.off('mouse:down', this.onPanMouseDown);
		this.canvas.off('mouse:move', this.onPanMouseMove);
		this.canvas.off('mouse:up', this.onPanMouseUp);

		if (this.canvas) {
			this.canvas.dispose();
		}
	}
}
